import { BaseRemoteMethod } from 'eurogard-remote-method';
import { TableEvent } from 'eurogard-data-source/table';
import { coerceString } from '@rxap/utilities';
import type { OpenApiRemoteMethod } from 'eurogard-open-api-remote-method';
import { PaginatorLike } from 'eurogard-data-source/pagination';
import { InjectionToken } from '@angular/core';
import { I18nService } from 'angular-services/user-area/i18n.service';

export const PAGE_ADAPTER_OPTIONS = new InjectionToken<string>('PAGE_ADAPTER_OPTIONS');

export interface GetPageRemoteMethodParameters {
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: 'desc' | 'asc' | undefined | '';
  filter: string;
}

export interface GetPageResponse<Data extends Record<string, any>> extends GetPageRemoteMethodParameters {
  total: number;
  rows: Data[];
}

export interface GetPageAdapterDefaultOptions {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  noArchiveFilter?: boolean;
}

export class GetPageAdapterRemoteMethod<Data extends Record<string, any>>
  extends BaseRemoteMethod<Data[], TableEvent> {

  constructor(
    private readonly remoteMethod: OpenApiRemoteMethod<GetPageResponse<Data>, GetPageRemoteMethodParameters>,
    private readonly paginator?: PaginatorLike,
    private readonly options?: GetPageAdapterDefaultOptions,
    private readonly acceptLanguage?: string,
  ) {
    super(null, remoteMethod.metadata);
  }

  /**
   * converts the record of filter to an string
   *
   * @example
   * { username: "max", age: 2 }
   * [ "username|max", "age|2" ]
   */
  public buildFilter(filterEvent?: string | Record<string, any> | null): string[] {
    const filter: string[] = [];
    if (!filterEvent || typeof filterEvent === 'string') {
      if (this.options?.noArchiveFilter !== true) {
        filter.push('__archived:false');
      }
    } else {
      if (this.options?.noArchiveFilter !== true) {
        if (!filterEvent['__archived']) {
          filter.push([ '__archived', 'false' ].join('|'));
        }
      }
      for (const [ key, value ] of Object.entries(filterEvent)) {
        if (value !== null && value !== undefined && `${ value }` !== '') {
          const valueString = coerceString(value);
          if (valueString) {
            filter.push([ key, valueString ].join('|'));
          }
        }
      }
    }
    return filter;
  }

  protected async _call(event: TableEvent): Promise<Data[]> {
    const sortBy                                    = event.sort?.active ?? this.options?.sortBy ?? '__updatedAt';
    const parameters: GetPageRemoteMethodParameters = {
      pageIndex: event.page?.pageIndex ?? 0,
      pageSize: event.page?.pageSize ?? 10,
      sortBy,
      // use the || operator to set the default sort direction if the sort direction is undefined or an empty string
      sortDirection: (sortBy === '__updatedAt' ? 'desc' : event.sort?.direction) ||
        (this.options?.sortDirection ?? 'asc'),
      acceptLanguage: this.acceptLanguage,
      filter: this.buildFilter(event.filter),
      ...(event.parameters ?? {}),
    };
    const response                                  = await this.remoteMethod.call({parameters});

    if (event.setTotalLength) {
      event.setTotalLength(response.total);
    }

    if (this.paginator) {
      this.paginator.length = response.total;
    }

    return response.rows;
  }

}

export function GetPageAdapterFactory(remoteMethod: OpenApiRemoteMethod, paginator?: PaginatorLike) {
  return new GetPageAdapterRemoteMethod(remoteMethod, paginator);
}

export function GetPageAdapterFactoryWithOptions(options: GetPageAdapterDefaultOptions = {}, i18nService?: I18nService) {
  return (remoteMethod: OpenApiRemoteMethod, paginator?: PaginatorLike) => {
    return new GetPageAdapterRemoteMethod(remoteMethod, paginator, options, i18nService?.currentLanguage());
  };
}
